// src/App.jsx
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ConfigProvider } from "antd";
import { Provider, useSelector, useDispatch } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { theme } from "./config/theme";
import { store } from "./store";
import { initializeAuth } from "./store/auth/authSlice";
import { useGetMyProfileQuery } from "./store/api/apiSlice";
import { selectActiveUploadsCount } from "./store/upload/uploadSlice";
import ProtectedRoute from "./routes/ProtectedRoute";

// Layouts
import AdminLayout from "./components/layout/AdminLayout";
import OrganizationLayout from "./components/layout/OrganizationLayout";
import InterpretersLayout from "./components/layout/InterpretersLayout";
import SurveyLayout from "./components/layout/SurveyLayout";
import PublicLayout from "./components/layout/PublicLayout";

// Pages
import Login from "./pages/auth/Login";
import LoadingScreen from "./components/common/LoadingScreen";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsAndConditions from "./pages/legal/TermsAndConditions";
import OrganizationTermsAndConditions from "./pages/legal/OrganizationTermsAndConditions";
import OrganizationPrivacyPolicy from "./pages/legal/OrganizationPrivacyPolicy";
import LandingPage from "./pages/LandingPage/LandingPage";
import FaqPage from "./pages/LandingPage/FaqPage";
import InTheNewsPage from "./pages/LandingPage/InTheNewsPage";
import CampaingSurveyLayout from "./components/layout/CampaingSurveyLayout";
import ResetPassword from "./pages/auth/ResetPassword";

// Component
import CookieBanner from "./components/CookieBanner/CookieBanner";
import GlobalUploadBanner from "./components/common/GlobalUploadBanner";
import { useScrollToTop } from "./hooks/useScrollToTop";

const AuthInitializer = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { initialLoading, token, role } = useSelector((state) => ({
    initialLoading: state.auth.initialLoading,
    token: state.auth.token,
    role: state.auth.role,
  }));

  const isPublicSurvey =
    location.pathname.startsWith("/form-submissions") ||
    location.pathname.startsWith("/campaign-submissions");

  const shouldFetchProfile =
    !initialLoading &&
    Boolean(token) &&
    Boolean(role) &&
    !isPublicSurvey;

  useGetMyProfileQuery(shouldFetchProfile ? role : skipToken, {
    skip: !shouldFetchProfile,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  if (initialLoading) {
    return <LoadingScreen />;
  }

  return children;
};

// Component to block tab close during active uploads
const UploadProtection = () => {
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Get the current state directly from the store to ensure we have the latest count
      const state = store.getState();
      const currentUploadsCount = selectActiveUploadsCount(state);

      if (currentUploadsCount > 0) {
        // Standard way to show browser's confirmation dialog
        e.preventDefault();
        // Modern browsers ignore custom messages and show their own
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []); // Empty dependency array - we'll get state directly from store

  return null;
};

const AppRoutes = () => {
  const { token } = useSelector((state) => state.auth);

  return (
    <Routes>
      <Route path="/" element={!token ? <LandingPage /> : <AuthRedirect />} />
      <Route path="/faqs" element={<FaqPage />} />
      <Route path="/in-the-news" element={<InTheNewsPage />} />
      <Route path="/auth/login" element={<Login />} />
      <Route
        path="/privacy-policy"
        element={
          <PublicLayout>
            <PrivacyPolicy />
          </PublicLayout>
        }
      />
      <Route
        path="/terms-and-conditions"
        element={
          <PublicLayout>
            <TermsAndConditions />
          </PublicLayout>
        }
      />
      <Route
        path="/organization-terms-and-conditions/:formId?"
        element={<OrganizationTermsAndConditions />}
      />
      <Route
        path="/organization-privacy-policy/:formId?"
        element={<OrganizationPrivacyPolicy />}
      />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      />

      <Route
        path="/organisation/*"
        element={
          <ProtectedRoute allowedRoles={["organization"]}>
            <OrganizationLayout />
          </ProtectedRoute>
        }
      />

      {/* Interpreters Routes */}
      <Route
        path="/interpreter/*"
        element={
          <ProtectedRoute allowedRoles={["interpreter"]}>
            <InterpretersLayout />
          </ProtectedRoute>
        }
      />

      {/* Survey Routes */}
      <Route path="/form-submissions/*" element={<SurveyLayout />} />
      <Route
        path="/campaign-submissions/*"
        element={<CampaingSurveyLayout />}
      />

      {/* Default Redirects */}
      <Route path="/" element={<AuthRedirect />} />
      <Route path="*" element={<Navigate to="/auth/login" replace />} />
    </Routes>
  );
};

const RouteScrollHandler = () => {
  useScrollToTop();
  return null;
};

const App = () => {
  return (
    <Provider store={store}>
      <ConfigProvider
        theme={theme}
        getPopupContainer={(node) => {
          if (node) {
            return node.parentNode;
          }
          return document.body;
        }}
      >
        <BrowserRouter>
          <AuthInitializer>
            <RouteScrollHandler />
            <UploadProtection />
            <GlobalUploadBanner />
            <CookieBanner />
            <AppRoutes />
          </AuthInitializer>
        </BrowserRouter>
      </ConfigProvider>
    </Provider>
  );
};

const AuthRedirect = () => {
  const { user, token, role } = useSelector((state) => state.auth);

  if (!token) {
    return <Navigate to="/auth/login" replace />;
  }

  const roleRedirects = {
    admin: "/admin/dashboard",
    organization: "/organisation/dashboard",
    interpreter: "/interpreter/dashboard",
  };

  return (
    <Navigate to={roleRedirects[user?.type || role] || "/auth/login"} replace />
  );
};

export default App;
